import React, { useEffect, useState } from 'react';
import './AccessDeniedModal.css';
import { useAuth } from '../../contexts/AuthContext';
import { requestPresentationAccess } from '../../services/presentationRequestService';

interface AccessDeniedModalProps {
  isOpen: boolean;
  onClose: () => void;
  packageName: string;
  dataPackageId?: string;
  context?: string;
}

type RequestStatus = 'form' | 'sending' | 'sent';

function normalizePhoneInput(value: string): string {
  return value.replace(/[^\d+\s()-]/g, '').slice(0, 24);
}

function isValidPhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 10;
}

const AccessDeniedModal: React.FC<AccessDeniedModalProps> = ({
  isOpen,
  onClose,
  packageName,
  dataPackageId,
  context,
}) => {
  const { user } = useAuth();
  const [status, setStatus] = useState<RequestStatus>('form');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setStatus('form');
      setPhone('');
      setPhoneError(null);
      setSubmitError(null);
      return;
    }

    setStatus('form');
    setPhone(user?.phone || '');
    setPhoneError(null);
    setSubmitError(null);
  }, [isOpen, user?.phone]);

  const handleSubmit = async () => {
    const trimmedPhone = phone.trim();
    if (!isValidPhone(trimmedPhone)) {
      setPhoneError('Укажите номер телефона для обратной связи (не менее 10 цифр)');
      return;
    }

    setPhoneError(null);
    setSubmitError(null);
    setStatus('sending');

    const result = await requestPresentationAccess({
      packageName,
      dataPackageId,
      context: context || packageName,
      phone: trimmedPhone,
    });

    if (result.ok) {
      setStatus('sent');
      return;
    }

    setSubmitError(result.error || 'Не удалось отправить запрос');
    setStatus('form');
  };

  if (!isOpen) return null;

  return (
    <div className="access-denied-overlay" onClick={onClose}>
      <div className="access-denied-modal" onClick={(e) => e.stopPropagation()}>
        <div className="access-denied-header">
          <div className="access-denied-icon">
            <i className={`fas ${status === 'sent' ? 'fa-check' : 'fa-lock'}`}></i>
          </div>
          <h2 className="access-denied-title">
            {status === 'sent' ? 'Запрос отправлен' : 'Запрос презентации'}
          </h2>
          <button className="access-denied-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="access-denied-body">
          <p className="access-denied-package">
            Пакет: <span className="package-name">{packageName}</span>
          </p>

          {(status === 'form' || status === 'sending') && (
            <>
              <p className="access-denied-message">
                Оставьте номер телефона — команда datacircle свяжется с вами для обсуждения доступа и презентации.
              </p>
              {submitError && <p className="access-denied-phone-error">{submitError}</p>}
              <label className="access-denied-phone-label" htmlFor="presentation-request-phone">
                Телефон для обратной связи <span className="required">*</span>
              </label>
              <input
                id="presentation-request-phone"
                className={`access-denied-phone-input${phoneError ? ' access-denied-phone-input--error' : ''}`}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+7 (999) 123-45-67"
                value={phone}
                disabled={status === 'sending'}
                onChange={(e) => {
                  setPhone(normalizePhoneInput(e.target.value));
                  if (phoneError) setPhoneError(null);
                  if (submitError) setSubmitError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              {phoneError && <p className="access-denied-phone-error">{phoneError}</p>}
            </>
          )}

          {status === 'sending' && (
            <p className="access-denied-message">Отправляем запрос на презентацию…</p>
          )}

          {status === 'sent' && (
            <>
              <p className="access-denied-message">
                Мы получили ваш запрос. Команда datacircle свяжется с вами по указанному телефону в ближайшее время.
              </p>
              <p className="access-denied-message">
                Также вы можете задать интересующий вас вопрос, написав нам на email:
              </p>
              <div className="access-denied-contact">
                <div className="contact-item">
                  <i className="fas fa-envelope"></i>
                  <a href="mailto:fin@zarya.vc">fin@zarya.vc</a>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="access-denied-footer">
          {status === 'form' ? (
            <>
              <button type="button" className="btn-cancel" onClick={onClose}>
                Отмена
              </button>
              <button type="button" className="btn-understood" onClick={handleSubmit}>
                <i className="fas fa-paper-plane"></i>
                Отправить запрос
              </button>
            </>
          ) : (
            <button type="button" className="btn-understood" onClick={onClose} disabled={status === 'sending'}>
              <i className="fas fa-check-circle"></i>
              {status === 'sending' ? 'Отправка…' : 'Понятно'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessDeniedModal;
